import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getMe,
  getStoredCustomer,
  loginWithPassword,
  logout as svcLogout,
  requestCode as svcRequestCode,
  setStoredCustomer,
  verifyCode,
  type AuthChannel,
} from '../../services/auth';
import { getAccessToken } from '../../services/api';
import type { User } from '../../types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (contact: string, code: string, channel: AuthChannel) => Promise<void>;
  loginPassword: (contact: string, password: string, channel: AuthChannel) => Promise<void>;
  logout: () => Promise<void>;
  requestCode: (contact: string, channel: AuthChannel) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => getStoredCustomer());
  const [loading, setLoading] = useState<boolean>(() => Boolean(getAccessToken()));

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getMe()
      .then((u) => {
        if (cancelled) return;
        setUser(u);
        setStoredCustomer(u);
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        setStoredCustomer(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (contact: string, code: string, channel: AuthChannel) => {
    const u = await verifyCode(contact, code, channel);
    setUser(u);
  }, []);

  const loginPassword = useCallback(async (contact: string, password: string, channel: AuthChannel) => {
    const u = await loginWithPassword(contact, password, channel);
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    await svcLogout();
    setUser(null);
  }, []);

  const requestCode = useCallback(async (contact: string, channel: AuthChannel) => {
    await svcRequestCode(contact, channel);
  }, []);

  const refresh = useCallback(async () => {
    const u = await getMe();
    setUser(u);
    setStoredCustomer(u);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, loginPassword, logout, requestCode, refresh }),
    [user, loading, login, loginPassword, logout, requestCode, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return ctx;
}
